import { memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { STATUS_MESSAGE_AUTO_DISMISS_MS } from '../../constants/duration';
import { useElapse } from '../../hooks/useElapse';
import { useToggle } from '../../hooks/useToggle';
import { BaseStatusMessageProps } from '../../types/BaseStatusMessageProps';
import { IconType } from '../../types/IconType';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Box } from '../Box/Box';
import { CollapsibleText } from '../CollapsibleText/CollapsibleText';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Markdown } from '../Markdown/Markdown';
import { Text } from '../Text/Text';

interface Props extends BaseStatusMessageProps {
  /** aria-live to indicate the type of announcement of the live placeholder region */
  ariaLive?: 'assertive' | 'polite';
  /** Specifies the limit messsage length before using a CollapsibleText */
  limit?: number;
  /** supported modes */
  mode?: 'banner' | 'note' | 'column';
}

export const StatusMessage = memo(
  ({
    action,
    ariaLive,
    autoDismiss,
    limit,
    message,
    mode = 'note',
    status = 'info',
    title,
    onDismiss,
    ...rest
  }: Props) => {
    const [isVisible, _toggleIsVisible, _show, dismiss] = useToggle(true);
    const intl = useIntl();

    const handleDismiss = useCallback(() => {
      onDismiss?.();
      dismiss();
    }, [dismiss, onDismiss]);

    const actions = useMemo(() => {
      const actions: UserAction[] = [];
      if (action) {
        actions.push(action);
      }
      if (onDismiss) {
        actions.push({
          icon: 'x',
          mode: 'icon',
          text: intl.formatMessage({
            id: 'StatusMessage.dismiss',
            defaultMessage: 'Dismiss',
            description: 'Button label to dismiss a status message',
          }),
          tooltip: intl.formatMessage({
            id: 'StatusMessage.dismissTooltip',
            defaultMessage: 'Dismiss',
            description: 'Tooltip for dismissing a status message',
          }),
          onClick: handleDismiss,
        });
      }
      return actions;
    }, [action, handleDismiss, intl, onDismiss]);

    useElapse(STATUS_MESSAGE_AUTO_DISMISS_MS, {
      disabled: !autoDismiss,
      callback: handleDismiss,
    });

    const styles = useStyles();

    if (!isVisible) {
      return null;
    }

    return (
      <Layout
        {...rest}
        align={mode === 'column' ? 'start' : 'center'}
        aria-live={ariaLive}
        border="border"
        borderRadius={mode === 'banner' ? undefined : 'm'}
        direction={mode === 'column' ? 'column' : 'row'}
        justify="space-between"
        px={4}
        py={3}
        role="alert"
        spacing={4}
        styles={styles.colors.status}
        styleProps={{ status }}
      >
        <Layout align="center" spacing={2}>
          <Icon icon={`status-${status}` as IconType} />
          <Layout direction="column">
            {title && <Text variant="body-bold">{title}</Text>}
            {limit ? (
              <CollapsibleText
                limit={limit}
                text={message}
                whiteSpace="pre-wrap"
              />
            ) : (
              <Markdown text={message} />
            )}
          </Layout>
        </Layout>
        {actions.length > 0 && (
          <Box
            ml={mode === 'column' ? 3 : undefined}
            styles={styles.sticky.top(4)}
          >
            <Actions actions={actions} />
          </Box>
        )}
      </Layout>
    );
  },
);
